/**
 * Products domain — React Query hooks.
 *
 * Read hooks are re-exported directly from the generated client (with a
 * few hand-written wrappers where drf-spectacular's auto-wrapping
 * produces the wrong response type).
 *
 * Write hooks are wrapped via `useInvalidatingMutation` so cache
 * invalidation is declarative and compile-time-enforced via the
 * `ProductsMutationId` registry in `./invalidation`.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import type { VariantCatalog, VariantsCatalogResponse } from './utils'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListProductsCatalog,
  listProductsCatalogQueryKey,
  listVariantsCatalogQueryKey,
  useGetVariantDetail,
  getVariantDetailQueryKey,
  useListVariantImages,
  listVariantImagesQueryKey,
  useListBrands,
  listBrandsQueryKey,
  useGetBrandDetail,
  getBrandDetailQueryKey,
  getSyndigoSyncStatusQueryKey,
  listSyndigoCategoriesQueryKey,
  listSyndigoBrandsQueryKey,
  useGetBulkSpecsheetStatus,
  getBulkSpecsheetStatusQueryKey,
  useListLegacyProducts,
  listLegacyProductsQueryKey,
  useListProductsPredictions,
  listProductsPredictionsQueryKey,
} from '../generated/hooks/productsController'

// ── Generated query/mutation parameter & response type re-exports ──

export type {
  ListProductsCatalogQueryResponse,
  ListProductsCatalogQueryParams,
} from '../generated/types/ListProductsCatalog'
export type {
  ListVariantsCatalogQueryResponse,
  ListVariantsCatalogQueryParams,
} from '../generated/types/ListVariantsCatalog'
export type {
  GetVariantDetailQueryResponse,
  GetVariantDetailPathParams,
} from '../generated/types/GetVariantDetail'
export type {
  CreateVariantMutationRequest,
  CreateVariantMutationResponse,
} from '../generated/types/CreateVariant'
export type {
  UpdateVariantMutationRequest,
  UpdateVariantMutationResponse,
  UpdateVariantPathParams,
} from '../generated/types/UpdateVariant'
export type {
  DeleteVariantMutationResponse,
  DeleteVariantPathParams,
} from '../generated/types/DeleteVariant'
export type {
  CreateBrandMutationRequest,
  CreateBrandMutationResponse,
} from '../generated/types/CreateBrand'
export type {
  UpdateBrandMutationRequest,
  UpdateBrandMutationResponse,
  UpdateBrandPathParams,
} from '../generated/types/UpdateBrand'
export type {
  TriggerSyndigoSyncMutationResponse,
  TriggerSyndigoSyncPathParams,
} from '../generated/types/TriggerSyndigoSync'
export type {
  ExportVariantsCatalogMutationRequest,
  ExportVariantsCatalogMutationResponse,
} from '../generated/types/ExportVariantsCatalog'
export type {
  ExportProductsCatalogMutationRequest,
  ExportProductsCatalogMutationResponse,
} from '../generated/types/ExportProductsCatalog'
export type {
  GetBulkSpecsheetStatusQueryResponse,
  GetBulkSpecsheetStatusPathParams,
} from '../generated/types/GetBulkSpecsheetStatus'
export type {
  ListLegacyProductsQueryResponse,
  ListLegacyProductsQueryParams,
} from '../generated/types/ListLegacyProducts'
export type {
  CreateLegacyProductMutationRequest,
  CreateLegacyProductMutationResponse,
} from '../generated/types/CreateLegacyProduct'
export type {
  UpdateLegacyProductMutationRequest,
  UpdateLegacyProductMutationResponse,
  UpdateLegacyProductPathParams,
} from '../generated/types/UpdateLegacyProduct'
export type { DeleteLegacyProductPathParams } from '../generated/types/DeleteLegacyProduct'
export type {
  ListProductsPredictionsQueryResponse,
  ListProductsPredictionsQueryParams,
} from '../generated/types/ListProductsPredictions'

// ── Internal imports for hand-written hooks + mutation wrappers ────

// Re-imported with `_` prefix so the hand-written wrappers below can use
// the same name space the generated module exports without colliding
// with the public re-exports at the top of this file.
import {
  listVariantsCatalogQueryKey as _listVariantsCatalogQueryKey,
  listSyndigoCategoriesQueryKey as _listSyndigoCategoriesQueryKey,
  listSyndigoBrandsQueryKey as _listSyndigoBrandsQueryKey,
  getSyndigoSyncStatusQueryKey as _getSyndigoSyncStatusQueryKey,
} from '../generated/hooks/productsController'
import { listVariantsCatalog } from '../generated/clients/listVariantsCatalog'
import { listSyndigoCategories } from '../generated/clients/listSyndigoCategories'
import { listSyndigoBrands } from '../generated/clients/listSyndigoBrands'
import { getSyndigoSyncStatus as getSyndigoSyncStatusClient } from '../generated/clients/getSyndigoSyncStatus'
import type { SyndigoSyncStatusResponse } from '../generated/types/SyndigoSyncStatusResponse'
import { createVariant } from '../generated/clients/createVariant'
import { updateVariant } from '../generated/clients/updateVariant'
import { deleteVariant } from '../generated/clients/deleteVariant'
import { createBrand } from '../generated/clients/createBrand'
import { updateBrand } from '../generated/clients/updateBrand'
import { deleteBrand } from '../generated/clients/deleteBrand'
import { bulkMatchVariants } from '../generated/clients/bulkMatchVariants'
import { bulkAssignBrand } from '../generated/clients/bulkAssignBrand'
import { setVariantMainImage } from '../generated/clients/setVariantMainImage'
import { unsetVariantMainImage } from '../generated/clients/unsetVariantMainImage'
import { triggerSyndigoSync } from '../generated/clients/triggerSyndigoSync'
import { exportVariantsCatalog } from '../generated/clients/exportVariantsCatalog'
import { exportProductsCatalog } from '../generated/clients/exportProductsCatalog'
import { createLegacyProduct } from '../generated/clients/createLegacyProduct'
import { updateLegacyProduct } from '../generated/clients/updateLegacyProduct'
import { deleteLegacyProduct } from '../generated/clients/deleteLegacyProduct'

import type {
  CreateVariantMutationRequest,
  UpdateVariantMutationRequest,
  UpdateVariantPathParams,
  DeleteVariantPathParams,
  CreateBrandMutationRequest,
  UpdateBrandMutationRequest,
  UpdateBrandPathParams,
  DeleteBrandPathParams,
} from '../generated/types'
import type { ListVariantsCatalogQueryParams } from '../generated/types/ListVariantsCatalog'
import type { BulkMatchVariantsMutationRequest } from '../generated/types/BulkMatchVariants'
import type { BulkAssignBrandMutationRequest } from '../generated/types/BulkAssignBrand'
import type { SetVariantMainImagePathParams } from '../generated/types/SetVariantMainImage'
import type { UnsetVariantMainImagePathParams } from '../generated/types/UnsetVariantMainImage'
import type { TriggerSyndigoSyncPathParams } from '../generated/types/TriggerSyndigoSync'
import type { ExportVariantsCatalogMutationRequest } from '../generated/types/ExportVariantsCatalog'
import type { ExportProductsCatalogMutationRequest } from '../generated/types/ExportProductsCatalog'
import type { CreateLegacyProductMutationRequest } from '../generated/types/CreateLegacyProduct'
import type {
  UpdateLegacyProductMutationRequest,
  UpdateLegacyProductPathParams,
} from '../generated/types/UpdateLegacyProduct'
import type { DeleteLegacyProductPathParams } from '../generated/types/DeleteLegacyProduct'
import type { LabelValue } from '../generated/types/LabelValue'

// ── Hand-written query hooks ───────────────────────────────────────

/**
 * List variants catalog.
 *
 * Hand-written wrapper around the generated `listVariantsCatalog` client
 * function because drf-spectacular auto-wraps ViewSet `list()` action
 * responses in `array<item>`, producing a generated type that doesn't
 * match the actual runtime response shape. The runtime response is the
 * `VariantsCatalogResponse` wrapper itself — this wrapper types it
 * correctly.
 */
export function useListVariantsCatalog(
  params?: ListVariantsCatalogQueryParams,
  options: {
    query?: Omit<
      UseQueryOptions<VariantsCatalogResponse, Error>,
      'queryKey' | 'queryFn'
    >
  } = {}
) {
  const { query = {} } = options
  return useQuery<VariantsCatalogResponse, Error>({
    queryKey: _listVariantsCatalogQueryKey(params),
    queryFn: async ({ signal }) =>
      (await listVariantsCatalog(params, {
        signal,
      })) as unknown as VariantsCatalogResponse,
    ...query,
  })
}

/**
 * List distinct Syndigo categories as `{label, value}` options.
 *
 * Hand-written wrapper that adds `refetchInterval: 10_000` (poll every
 * 10 seconds) so the UI can display sync progress in real time. The
 * generated hook doesn't include this because polling behavior is a
 * frontend concern, not an OpenAPI spec concern.
 */
export function useGetSyndigoSyncStatus(
  options: {
    query?: Omit<
      UseQueryOptions<SyndigoSyncStatusResponse, Error>,
      'queryKey' | 'queryFn'
    >
  } = {}
) {
  const { query = {} } = options
  return useQuery<SyndigoSyncStatusResponse, Error>({
    queryKey: _getSyndigoSyncStatusQueryKey(),
    queryFn: async ({ signal }) =>
      (await getSyndigoSyncStatusClient({
        signal,
      })) as unknown as SyndigoSyncStatusResponse,
    refetchInterval: 10_000,
    ...query,
  })
}

/**
 * List distinct Syndigo categories as `{label, value}` options.
 *
 * Hand-written wrapper — drf-spectacular auto-wraps the bare-array
 * response from the `@action` in a paginated shape. Runtime returns
 * `LabelValue[]`.
 */
export function useListSyndigoCategories(
  options: {
    query?: Omit<UseQueryOptions<LabelValue[], Error>, 'queryKey' | 'queryFn'>
  } = {}
) {
  const { query = {} } = options
  return useQuery<LabelValue[], Error>({
    queryKey: _listSyndigoCategoriesQueryKey(),
    queryFn: async ({ signal }) =>
      (await listSyndigoCategories(undefined, {
        signal,
      })) as unknown as LabelValue[],
    ...query,
  })
}

/**
 * List distinct Syndigo brands as `{label, value}` options.
 *
 * Hand-written wrapper — same reason as `useListSyndigoCategories`.
 */
export function useListSyndigoBrands(
  options: {
    query?: Omit<UseQueryOptions<LabelValue[], Error>, 'queryKey' | 'queryFn'>
  } = {}
) {
  const { query = {} } = options
  return useQuery<LabelValue[], Error>({
    queryKey: _listSyndigoBrandsQueryKey(),
    queryFn: async ({ signal }) =>
      (await listSyndigoBrands(undefined, {
        signal,
      })) as unknown as LabelValue[],
    ...query,
  })
}

/**
 * Infinite-query wrapper around the generated `listVariantsCatalog`
 * client fn.
 *
 * There is exactly one consumer (`MobileSelectProductVariantModal`).
 * Rather than configure Kubb to emit `useInfiniteQuery` variants for
 * every list endpoint, this small helper uses the generated client
 * function with `useInfiniteQuery`.
 */
export function useVariantsCatalogInfinite(
  params: ListVariantsCatalogQueryParams = {},
  pageSize: number = 50,
  enabled: boolean = true
) {
  return useInfiniteQuery<VariantsCatalogResponse, Error>({
    initialPageParam: 1,
    queryKey: ['variants-catalog-infinite', params, pageSize] as const,
    queryFn: async ({ pageParam }) =>
      (await listVariantsCatalog({
        ...params,
        page: pageParam as number,
        page_size: pageSize,
      })) as unknown as VariantsCatalogResponse,
    getNextPageParam: (last, all) =>
      last.results.length < pageSize ? undefined : all.length + 1,
    enabled,
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useCreateVariant() {
  return useInvalidatingMutation('createVariant', invalidationRegistry, {
    mutationFn: (variables: { data: CreateVariantMutationRequest }) =>
      createVariant(variables.data),
  })
}

export function useUpdateVariant() {
  return useInvalidatingMutation('updateVariant', invalidationRegistry, {
    mutationFn: (variables: {
      id: UpdateVariantPathParams['id']
      data: UpdateVariantMutationRequest
    }) => updateVariant({ id: variables.id }, variables.data),
  })
}

export function useDeleteVariant() {
  return useInvalidatingMutation('deleteVariant', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteVariantPathParams['id'] }) =>
      deleteVariant({ id: variables.id }),
  })
}

export function useCreateBrand() {
  return useInvalidatingMutation('createBrand', invalidationRegistry, {
    mutationFn: (variables: { data: CreateBrandMutationRequest }) =>
      createBrand(variables.data),
  })
}

export function useUpdateBrand() {
  return useInvalidatingMutation('updateBrand', invalidationRegistry, {
    mutationFn: (variables: {
      id: UpdateBrandPathParams['id']
      data: UpdateBrandMutationRequest
    }) => updateBrand({ id: variables.id }, variables.data),
  })
}

export function useDeleteBrand() {
  return useInvalidatingMutation('deleteBrand', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteBrandPathParams['id'] }) =>
      deleteBrand({ id: variables.id }),
  })
}

/**
 * Bulk-match variants to products (or unlink).
 *
 * This hook uses raw `useMutation` instead of `useInvalidatingMutation`
 * because it needs:
 *   - **Optimistic updates**: immediately reflect link/unlink changes in
 *     the variant table before the server responds.
 *   - **Rollback on error**: restore the snapshot if the request fails.
 *   - **Conditional invalidation**: skip cache invalidation when the
 *     server returns an async job (HTTP 202 / `{ async: true }`) since
 *     the data hasn't actually changed yet — the background job will
 *     trigger changes later.
 */
export function useBulkMatchVariants() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: { data: BulkMatchVariantsMutationRequest }) =>
      bulkMatchVariants(variables.data),

    onMutate: async (variables) => {
      const request = variables.data as BulkMatchVariantsMutationRequest & {
        _productName?: string
      }

      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        predicate: (query) => isVariantsCatalogQuery(query.queryKey),
      })

      // Snapshot current data for rollback
      const previousData = queryClient.getQueriesData<VariantsCatalogResponse>({
        predicate: (query) => isVariantsCatalogQuery(query.queryKey),
      })

      // Optimistically update the cache
      queryClient.setQueriesData<VariantsCatalogResponse>(
        { predicate: (query) => isVariantsCatalogQuery(query.queryKey) },
        (old) => {
          if (!old) return old
          return {
            ...old,
            results: old.results.map((variant) => {
              // Explicit matches mode
              if (request.matches) {
                const match = request.matches.find(
                  (m) => m.variant_id === variant.id
                )
                if (match) {
                  return {
                    ...variant,
                    product_id: match.product_id,
                    product_name: match.product_id
                      ? (request._productName ?? null)
                      : null,
                    match_status: match.product_id ? 'matched' : 'unmatched',
                  } as VariantCatalog
                }
              }
              // Filter-based unlink mode
              if (
                request.unlink_mode &&
                !request.exclude_ids?.includes(variant.id)
              ) {
                return {
                  ...variant,
                  product_id: null,
                  product_name: null,
                  match_status: 'unmatched',
                } as VariantCatalog
              }
              // Filter-based link mode
              if (
                request.product_id &&
                !request.exclude_ids?.includes(variant.id)
              ) {
                return {
                  ...variant,
                  product_id: request.product_id,
                  product_name: request._productName ?? null,
                  match_status: 'matched',
                } as VariantCatalog
              }
              return variant
            }),
          }
        }
      )

      return { previousData }
    },

    onError: (_error, _variables, _onMutateResult) => {
      // Rollback on error
      const ctx = _onMutateResult as
        | {
            previousData?: [unknown, VariantsCatalogResponse | undefined][]
          }
        | undefined
      if (ctx?.previousData) {
        ctx.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey as QueryKey, data)
        })
      }
    },

    onSettled: (data) => {
      // Only invalidate for synchronous operations (non-async).
      // When the server responds with 202 / { async: true }, the data
      // hasn't changed yet — a background job will apply the changes
      // later, and the next user-triggered refetch will pick them up.
      const isAsync = (data as { async?: boolean } | undefined)?.async
      if (isAsync) return

      // Synchronous success — run the same invalidation the registry
      // would have done.
      const rule = invalidationRegistry.bulkMatchVariants
      rule
        .invalidates({})
        .forEach((queryKey) => queryClient.invalidateQueries({ queryKey }))
      rule.removes?.({}).forEach((filters) => {
        queryClient.removeQueries(filters)
      })
    },
  })
}

/**
 * Predicate matching variant-catalog queries regardless of key shape
 * (generated Kubb key or legacy string key). Used by the optimistic-
 * update logic in `useBulkMatchVariants`.
 */
function isVariantsCatalogQuery(queryKey: QueryKey): boolean {
  if (!Array.isArray(queryKey) || queryKey.length === 0) return false
  const first = queryKey[0]
  // Kubb-generated keys are objects with `url`
  if (typeof first === 'object' && first !== null && 'url' in first) {
    return (first as { url: string }).url.includes('/catalog/variants/')
  }
  // Legacy string keys
  if (typeof first === 'string') {
    return first === 'variants-catalog' || first === 'variants-catalog-infinite'
  }
  return false
}

export function useBulkAssignBrand() {
  return useInvalidatingMutation('bulkAssignBrand', invalidationRegistry, {
    mutationFn: (variables: { data: BulkAssignBrandMutationRequest }) =>
      bulkAssignBrand(variables.data),
  })
}

export function useSetVariantMainImage() {
  return useInvalidatingMutation('setVariantMainImage', invalidationRegistry, {
    mutationFn: (variables: {
      variant_id: SetVariantMainImagePathParams['variant_id']
      document_id: SetVariantMainImagePathParams['document_id']
    }) =>
      setVariantMainImage({
        variant_id: variables.variant_id,
        document_id: variables.document_id,
      }),
  })
}

export function useUnsetVariantMainImage() {
  return useInvalidatingMutation(
    'unsetVariantMainImage',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        variant_id: UnsetVariantMainImagePathParams['variant_id']
        document_id: UnsetVariantMainImagePathParams['document_id']
      }) =>
        unsetVariantMainImage({
          variant_id: variables.variant_id,
          document_id: variables.document_id,
        }),
    }
  )
}

export function useTriggerSyndigoSync() {
  return useInvalidatingMutation('triggerSyndigoSync', invalidationRegistry, {
    mutationFn: (variables: {
      variant_id: TriggerSyndigoSyncPathParams['variant_id']
    }) => triggerSyndigoSync({ variant_id: variables.variant_id }),
  })
}

export function useExportVariantsCatalog() {
  return useInvalidatingMutation(
    'exportVariantsCatalog',
    invalidationRegistry,
    {
      mutationFn: (variables: { data: ExportVariantsCatalogMutationRequest }) =>
        exportVariantsCatalog(variables.data, { responseType: 'blob' }),
    }
  )
}

export function useExportProductsCatalog() {
  return useInvalidatingMutation(
    'exportProductsCatalog',
    invalidationRegistry,
    {
      mutationFn: (variables: { data: ExportProductsCatalogMutationRequest }) =>
        exportProductsCatalog(variables.data, { responseType: 'blob' }),
    }
  )
}

export function useCreateLegacyProduct() {
  return useInvalidatingMutation('createLegacyProduct', invalidationRegistry, {
    mutationFn: (variables: { data: CreateLegacyProductMutationRequest }) =>
      createLegacyProduct(variables.data),
  })
}

export function useUpdateLegacyProduct() {
  return useInvalidatingMutation('updateLegacyProduct', invalidationRegistry, {
    mutationFn: (variables: {
      id: UpdateLegacyProductPathParams['id']
      data: UpdateLegacyProductMutationRequest
    }) => updateLegacyProduct({ id: variables.id }, variables.data),
  })
}

export function useDeleteLegacyProduct() {
  return useInvalidatingMutation('deleteLegacyProduct', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteLegacyProductPathParams['id'] }) =>
      deleteLegacyProduct({ id: variables.id }),
  })
}
